import { DashboardLayout } from '@/components/app/DashboardLayout'
import { RiskLevelBadge } from '@/components/app/RiskLevelBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AuthAPI, AdminAPI } from '@/services/api'
import { useState, useMemo, useEffect } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  FileEdit,
  BarChart3,
  User,
  Baby,
  Mail,
  Clock,
  LogIn,
  UserPlus,
  Settings,
  UserX,
} from 'lucide-react'

function getAuditActionIcon(actionKey: string | undefined): LucideIcon {
  switch (actionKey) {
    case 'USER_LOGIN':
      return LogIn
    case 'REGISTERED_ACCOUNT':
      return UserPlus
    case 'UPDATED_ACCOUNT_PROFILE':
      return Settings
    case 'COMPLETED_ASSESSMENT':
      return CircleCheck
    case 'STARTED_ASSESSMENT':
      return FileEdit
    case 'RECEIVED_ASSESSMENT_RESULT':
      return BarChart3
    case 'ADDED_CHILD_PROFILE':
      return Baby
    case 'UPDATED_CHILD_PROFILE':
      return Settings
    case 'DEACTIVATED_CHILD_PROFILE':
      return UserX
    default:
      return User
  }
}

const AUDIT_REFRESH_MS = 30_000

export const AuditLogs = () => {
  const [user, setUser] = useState<any>(null)
  const [activityLogs, setActivityLogs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const fallbackAdminUser = { firstName: 'Admin', lastName: '', email: '', role: 'ADMIN' } as any

  useEffect(() => {
    let mounted = true

    const fetchData = async (showLoader = false) => {
      if (showLoader) {
        setIsLoading(true)
      }

      try {
        const [userResult, logsResult] = await Promise.allSettled([
          AuthAPI.me(),
          AdminAPI.getActivityLogs(),
        ])

        if (!mounted) {
          return
        }

        if (userResult.status === 'fulfilled') {
          setUser(userResult.value.user)
        } else {
          console.error('Failed to fetch session user for audit logs', userResult.reason)
        }

        if (logsResult.status === 'fulfilled') {
          setActivityLogs(logsResult.value)
        } else {
          console.error('Failed to fetch audit logs list', logsResult.reason)
          setActivityLogs([])
        }
      } catch (err) {
        console.error('Failed to fetch audit logs data', err)
      } finally {
        if (mounted && showLoader) {
          setIsLoading(false)
        }
      }
    }

    fetchData(true)
    const intervalId = setInterval(() => {
      fetchData(false)
    }, AUDIT_REFRESH_MS)

    return () => {
      mounted = false
      clearInterval(intervalId)
    }
  }, [])

  // Date formatting helper with relative time
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }
  }

  // Pagination calculations
  const totalPages = Math.ceil(activityLogs.length / itemsPerPage)
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return activityLogs.slice(startIndex, endIndex)
  }, [activityLogs, currentPage])

  if (isLoading) {
    return (
      <DashboardLayout user={user || fallbackAdminUser}>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout user={user || fallbackAdminUser}>
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
            Audit Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all system activities and user actions
          </p>
        </div>

        {/* Activity Logs */}
        <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Activity Timeline</CardTitle>
                <CardDescription>Detailed system activity history</CardDescription>
              </div>
              {totalPages > 1 && (
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activityLogs.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No activity logs found</p>
                </div>
              ) : (
                paginatedLogs.map((log, index) => {
                  const ActionIcon = getAuditActionIcon(log.actionKey)
                  return (
                    <div
                      key={log.id}
                      className="group flex flex-col gap-3 p-4 border border-transparent bg-card rounded-lg hover:border-border hover:shadow-md transition-all duration-300 animate-in slide-in-from-bottom dark:border-border"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-50 to-teal-100 text-primary group-hover:scale-110 transition-transform duration-300 dark:from-primary/20 dark:to-primary/10">
                          <ActionIcon className="h-5 w-5" aria-hidden />
                        </div>
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground">{log.user || 'Unknown User'}</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-sm text-muted-foreground">{log.action || 'Unknown Action'}</span>
                            {log.riskLevel && (
                              <>
                                <span className="text-muted-foreground">•</span>
                                <RiskLevelBadge riskLevel={log.riskLevel} size="sm" />
                              </>
                            )}
                          </div>
                          {(log.actionKey === 'USER_LOGIN' || log.actionKey === 'REGISTERED_ACCOUNT' || log.actionKey === 'UPDATED_ACCOUNT_PROFILE') ? null : (
                            <div className="text-sm text-muted-foreground">
                              <span className="font-medium">Child:</span> {log.child || 'N/A'}
                            </div>
                          )}
                          {log.email && (
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5">
                                <Mail className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                                {log.email}
                              </span>
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                            {formatDateTime(log.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, activityLogs.length)} of {activityLogs.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      const showPage = page === 1 || page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)

                      if (!showPage) {
                        if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="px-2 text-muted-foreground">...</span>
                        }
                        return null
                      }

                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      )
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="gap-1"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
